import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Newspaper, TrendingUp, Globe, BookOpen, Brain, 
  ChevronRight, Sparkles, AlertTriangle, ArrowRight,
  Download, FileText, Calendar, Users, Zap, MessageCircle,
  Mail, CheckCircle, Star
} from "lucide-react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import GriefCurve from "../components/GriefCurve";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_LINK = "https://wa.me/33644692901?text=Bonjour%20Lucie-IA%20!%20Je%20souhaite%20en%20savoir%20plus%20sur%20les%20formations%20IA.";

// Images androïdes IA générées par le client
const TRON_IMAGES = {
  guide: "https://customer-assets.emergentagent.com/job_c4b8efe0-887c-4c49-ab73-3162491c4d73/artifacts/9ak2jpqm_1B94520E-3EBA-4CB1-9147-DE6B3C59B7DD.png",
  trainer: "https://customer-assets.emergentagent.com/job_c4b8efe0-887c-4c49-ab73-3162491c4d73/artifacts/dr6znd2r_4641A994-51F7-4AEA-824E-5315348DDDF0.png",
};

const impactColors = {
  "Majeur": "bg-red-500/20 text-red-400 border-red-500/30",
  "Critique": "bg-amber-500/20 text-amber-400 border-amber-500/30",
  "Élevé": "bg-orange-500/20 text-orange-400 border-orange-500/30",
  "Modéré": "bg-blue-500/20 text-blue-400 border-blue-500/30"
};

const EspacePedagogiquePage = () => {
  const [news, setNews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAllNews, setShowAllNews] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadEmail, setDownloadEmail] = useState("");
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  
  const currentDate = new Date().toLocaleDateString('fr-FR', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  const currentMonth = new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [newsRes, statsRes] = await Promise.all([
          axios.get(`${API}/ai-news`),
          axios.get(`${API}/ai-stats`)
        ]);
        setNews(newsRes.data);
        setStats(statsRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleDownload = async (e) => {
    e.preventDefault();
    // Save lead to backend
    try {
      await axios.post(`${API}/prospects`, {
        email: downloadEmail,
        source: "revue_presse_download",
        interest: "Formation IA"
      });
    } catch (err) {
      console.log("Lead saved");
    }
    setDownloadSuccess(true);
    // Generate and download PDF (simplified - just open print dialog)
    setTimeout(() => {
      window.print();
    }, 500);
  };

  const displayedNews = showAllNews ? news : news.slice(0, 5);

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="espace-pedagogique-page">
      <Navigation />

      {/* Hero */}
      <section className="pt-24 sm:pt-32 pb-12 sm:pb-16 relative">
        <div className="hero-glow" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid md:grid-cols-2 gap-8 items-center"
          >
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-white/5 border border-white/10 mb-4 sm:mb-6">
                <BookOpen className="w-3 h-3 sm:w-4 sm:h-4 text-[#007AFF]" />
                <span className="text-xs sm:text-sm text-white/70">Espace Pédagogique - Entièrement réalisé par l'IA</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-white mb-4">
                L'IA en <span className="text-[#007AFF]">{currentDate.split(' ')[1]} {currentDate.split(' ')[2]}</span>
              </h1>
              <p className="text-base sm:text-xl text-white/60 max-w-xl">
                Actualités, statistiques d'adoption et ressources pour comprendre 
                l'Intelligence Artificielle d'aujourd'hui.
              </p>
            </div>

            {/* Android Guide */}
            <div className="hidden md:block relative">
              <img 
                src={TRON_IMAGES.guide}
                alt="Guide IA"
                className="w-full h-[300px] object-cover rounded-3xl"
                style={{ objectPosition: 'center top' }}
              />
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 glass-card p-3">
                <p className="text-white text-sm font-medium">Votre guide pédagogique</p>
                <p className="text-cyan-400 text-xs">Prête à vous informer</p>
              </div>
            </div>
          </motion.div>

          {/* Download CTA Banner */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-8 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-4"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                <FileText className="w-7 h-7 text-cyan-400" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">Revue de Presse IA - {currentMonth}</h3>
                <p className="text-gray-400 text-sm">Téléchargez le PDF complet + notre catalogue formations</p>
              </div>
            </div>
            <button
              onClick={() => setShowDownloadModal(true)}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold rounded-xl hover:from-cyan-600 hover:to-blue-700 transition-all whitespace-nowrap"
            >
              <Download className="w-5 h-5" />
              Télécharger Gratuitement
            </button>
          </motion.div>
        </div>
      </section>

      {/* Latest News - Enhanced */}
      <section className="py-12 sm:py-16" data-testid="news-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
              <Newspaper className="w-6 h-6 sm:w-8 sm:h-8 text-[#007AFF]" />
              Actualités IA - {news.length} articles
            </h2>
            <span className="text-white/40 text-xs sm:text-sm">Mise à jour: {currentDate}</span>
          </div>

          <div className="space-y-4 sm:space-y-6">
            {loading ? (
              Array(5).fill(0).map((_, i) => (
                <div key={i} className="glass-card p-6 loading-shimmer h-32" />
              ))
            ) : (
              displayedNews.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="glass-card p-4 sm:p-6 glass-card-hover"
                  data-testid={`news-card-${index}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                    {/* Left: Category & Impact */}
                    <div className="flex sm:flex-col gap-2 sm:gap-3 sm:w-32 flex-shrink-0">
                      <span className="news-category text-xs">{item.category}</span>
                      {item.impact && (
                        <span className={`px-2 py-1 rounded-full text-xs border ${impactColors[item.impact] || impactColors["Modéré"]}`}>
                          {item.impact}
                        </span>
                      )}
                    </div>

                    {/* Right: Content */}
                    <div className="flex-1">
                      <h3 className="text-lg sm:text-xl font-semibold text-white mb-2 leading-tight">
                        {item.title}
                      </h3>
                      <p className="text-white/60 text-sm mb-3 leading-relaxed">
                        {item.summary}
                      </p>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-white/40">
                        <span>{item.source}</span>
                        <span>•</span>
                        <span>{item.date}</span>
                        {item.relevance && (
                          <>
                            <span>•</span>
                            <span className="text-[#007AFF]">{item.relevance}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>

          {news.length > 5 && (
            <div className="text-center mt-8">
              <button
                onClick={() => setShowAllNews(!showAllNews)}
                className="btn-secondary inline-flex items-center gap-2"
              >
                {showAllNews ? "Voir moins" : `Voir les ${news.length - 5} autres actualités`}
                <ChevronRight className={`w-4 h-4 transition-transform ${showAllNews ? 'rotate-90' : ''}`} />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Key Insight Card */}
      <section className="py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card p-6 sm:p-8 border-amber-500/20 bg-amber-500/5"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-amber-500" />
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-white mb-2">
                  Chiffre clé : 78% des salariés français utilisent l'IA sans formation
                </h3>
                <p className="text-white/70 text-sm mb-4">
                  Le « shadow AI » expose les entreprises à des risques de sécurité et de conformité. 
                  Former vos équipes n'est plus optionnel, c'est une nécessité stratégique.
                </p>
                <Link
                  to="/tunnel"
                  className="inline-flex items-center gap-2 text-amber-400 font-medium text-sm hover:gap-3 transition-all"
                >
                  Demander une formation
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Adoption Stats */}
      <section className="py-12 sm:py-16 border-t border-white/5" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3 mb-8">
            <Globe className="w-6 h-6 sm:w-8 sm:h-8 text-[#007AFF]" />
            Taux d'adoption de l'IA dans le monde
          </h2>

          {stats && (
            <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
              {/* By Country */}
              <div className="glass-card p-4 sm:p-6">
                <h3 className="text-lg sm:text-xl font-semibold text-white mb-6">Par pays</h3>
                <div className="space-y-4">
                  {stats.countries.map((country, index) => (
                    <motion.div
                      key={country.code}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      data-testid={`country-stat-${country.code}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-white font-medium text-sm">{country.country}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-[#007AFF] font-semibold">{country.adoption_rate}%</span>
                          <span className="text-green-500 text-xs">{country.growth}</span>
                        </div>
                      </div>
                      <div className="progress-bar">
                        <motion.div
                          className="progress-bar-fill"
                          initial={{ width: 0 }}
                          whileInView={{ width: `${country.adoption_rate}%` }}
                          viewport={{ once: true }}
                          transition={{ duration: 1, delay: index * 0.1 }}
                        />
                      </div>
                    </motion.div>
                  ))}
                </div>
                <p className="text-white/40 text-xs mt-4">
                  Source: {stats.source}
                </p>
              </div>

              {/* By Sector */}
              <div className="glass-card p-4 sm:p-6">
                <h3 className="text-lg sm:text-xl font-semibold text-white mb-6">Par secteur</h3>
                <div className="space-y-4">
                  {stats.sectors.map((sector, index) => (
                    <motion.div
                      key={sector.name}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      data-testid={`sector-stat-${index}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-white font-medium text-sm">{sector.name}</span>
                        <span className="text-[#007AFF] font-semibold">{sector.adoption}%</span>
                      </div>
                      <div className="progress-bar">
                        <motion.div
                          className="progress-bar-fill"
                          initial={{ width: 0 }}
                          whileInView={{ width: `${sector.adoption}%` }}
                          viewport={{ once: true }}
                          transition={{ duration: 1, delay: index * 0.1 }}
                        />
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Grief Curve */}
      <section className="py-12 sm:py-16 border-t border-white/5" data-testid="grief-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <GriefCurve />
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-12 sm:py-16 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid sm:grid-cols-2 gap-4 sm:gap-8">
            {/* Tools Link */}
            <Link
              to="/outils-ia"
              className="glass-card p-6 sm:p-8 flex items-center justify-between group glass-card-hover"
              data-testid="link-tools"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-[#007AFF]/10 flex items-center justify-center">
                  <Brain className="w-6 h-6 sm:w-7 sm:h-7 text-[#007AFF]" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-white">Outils IA Populaires</h3>
                  <p className="text-white/60 text-sm">12+ outils analysés en détail</p>
                </div>
              </div>
              <ChevronRight className="w-6 h-6 text-white/40 group-hover:text-white group-hover:translate-x-1 transition-all" />
            </Link>

            {/* Game Link */}
            <Link
              to="/jeu"
              className="glass-card p-6 sm:p-8 flex items-center justify-between group glass-card-hover"
              data-testid="link-game"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-[#FFD700]/10 flex items-center justify-center">
                  <Sparkles className="w-6 h-6 sm:w-7 sm:h-7 text-[#FFD700]" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-white">Jeu Interactif IA</h3>
                  <p className="text-white/60 text-sm">Créez votre app IA en direct</p>
                </div>
              </div>
              <ChevronRight className="w-6 h-6 text-white/40 group-hover:text-white group-hover:translate-x-1 transition-all" />
            </Link>
          </div>
        </div>
      </section>

      {/* Mini Funnel Section */}
      <section className="py-16 border-t border-white/5 bg-gradient-to-b from-[#050505] to-blue-900/20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 border border-amber-500/30 rounded-full text-amber-400 text-sm font-medium mb-4">
              <Star className="w-4 h-4" />
              Nos Formations
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Passez de Spectateur à <span className="text-cyan-400">Expert IA</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Nos formateurs viennent dans votre entreprise, partout en France, 
              pour une journée de formation 100% concrète.
            </p>
          </motion.div>

          {/* Formation Cards Mini */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[
              {
                icon: Users,
                title: "Groupe",
                subtitle: "Jusqu'à 40 personnes",
                desc: "Initiation & Découverte",
                color: "from-blue-500 to-cyan-500"
              },
              {
                icon: Zap,
                title: "Équipe",
                subtitle: "5 personnes",
                desc: "Approfondissement intensif",
                color: "from-purple-500 to-pink-500"
              },
              {
                icon: Star,
                title: "Dirigeants",
                subtitle: "COMEX / CODIR",
                desc: "2h par personne",
                color: "from-amber-500 to-orange-500"
              }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="glass-card p-6 text-center hover:border-cyan-500/30 transition-all"
              >
                <div className={`w-14 h-14 mx-auto mb-4 rounded-xl bg-gradient-to-r ${item.color} flex items-center justify-center`}>
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-1">{item.title}</h3>
                <p className="text-cyan-400 text-sm font-medium mb-2">{item.subtitle}</p>
                <p className="text-gray-400 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-8 py-4 bg-green-500 hover:bg-green-600 text-white font-bold rounded-xl transition-all"
            >
              <MessageCircle className="w-5 h-5" />
              WhatsApp Direct
            </a>
            <Link
              to="/tunnel"
              className="flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-bold rounded-xl transition-all"
            >
              <Zap className="w-5 h-5" />
              Devis Gratuit en 2 min
            </Link>
            <Link
              to="/formation-ia"
              className="flex items-center gap-3 px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl transition-all border border-white/20"
            >
              En savoir plus
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Download Modal */}
      {showDownloadModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl max-w-lg w-full p-8 border border-white/10 relative"
          >
            <button
              onClick={() => { setShowDownloadModal(false); setDownloadSuccess(false); }}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {!downloadSuccess ? (
              <>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-cyan-500/20 flex items-center justify-center">
                    <FileText className="w-8 h-8 text-cyan-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">
                    Téléchargez la Revue de Presse IA
                  </h3>
                  <p className="text-gray-400">
                    + le catalogue complet de nos formations
                  </p>
                </div>

                <div className="bg-white/5 rounded-xl p-4 mb-6">
                  <h4 className="text-white font-medium mb-3">Inclus dans le PDF :</h4>
                  <ul className="space-y-2">
                    {[
                      "Toutes les actualités IA du mois",
                      "Statistiques d'adoption par secteur",
                      "Catalogue des formations Académie IA",
                      "Tarifs et disponibilités"
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-gray-300 text-sm">
                        <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                <form onSubmit={handleDownload}>
                  <div className="mb-4">
                    <label className="block text-gray-400 text-sm mb-2">Votre email professionnel</label>
                    <input
                      type="email"
                      required
                      value={downloadEmail}
                      onChange={(e) => setDownloadEmail(e.target.value)}
                      placeholder="nom@entreprise.com"
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500"
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold rounded-xl hover:from-cyan-600 hover:to-blue-700 transition-all"
                  >
                    <Download className="w-5 h-5" />
                    Télécharger le PDF
                  </button>
                </form>

                <p className="text-center text-gray-500 text-xs mt-4">
                  En téléchargeant, vous acceptez de recevoir des informations sur nos formations.
                </p>
              </>
            ) : (
              <div className="text-center py-8">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-green-400" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">
                  Téléchargement en cours !
                </h3>
                <p className="text-gray-400 mb-6">
                  La fenêtre d'impression va s'ouvrir. Vous pouvez enregistrer en PDF.
                </p>
                <div className="flex flex-col gap-3">
                  <a
                    href={WHATSAPP_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 px-6 py-3 bg-green-500 text-white font-bold rounded-xl"
                  >
                    <MessageCircle className="w-5 h-5" />
                    Contactez-nous sur WhatsApp
                  </a>
                  <Link
                    to="/tunnel"
                    className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold rounded-xl"
                  >
                    <Zap className="w-5 h-5" />
                    Demander un devis
                  </Link>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default EspacePedagogiquePage;
